﻿import { useEffect, useState } from 'react'
import './Hero.css'
import { useBookDemo } from './BookDemoContext'

const DEMO_INTERVIEW_URL = import.meta.env.VITE_DEMO_INTERVIEW_URL || ''

const DEMO_CONTEXT_POINTS = [
  {
    title: 'We start directly with the interview questions',
    body: 'Since this is a demo, we skip the ice-breaker and move straight in. In a live SmartHire interview, Sia opens with a warm-up conversation first.',
  },
  {
    title: 'Sia adapts to what you say',
    body: 'She listens to your answers and asks follow-up questions in real time — just like a human interviewer. No two interviews are identical.',
  },
  {
    title: 'There are no right or wrong answers',
    body: 'Sia is assessing how you think through situations, communicate your ideas, and handle workplace challenges.',
  },
  {
    title: 'Answer naturally — the more context, the better',
    body: 'Just speak as you would in a real conversation. The more detail you give, the more meaningful the experience.',
  },
  {
    title: 'Your responses are confidential',
    body: 'This session is used only to demonstrate how PossoBuild works. No data is stored or shared beyond this session.',
  },
]

export default function Hero() {
  const openBookDemo = useBookDemo()
  const [showDemoIntro, setShowDemoIntro] = useState(false)

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id)
    if (!element) {
      return
    }

    window.scrollTo({
      top: element.getBoundingClientRect().top + window.scrollY - 72,
      behavior: 'smooth',
    })
  }

  useEffect(() => {
    if (!showDemoIntro) {
      return
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [showDemoIntro])

  useEffect(() => {
    if (!showDemoIntro) {
      return
    }

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowDemoIntro(false)
      }
    }

    document.addEventListener('keydown', handleKeydown)
    return () => document.removeEventListener('keydown', handleKeydown)
  }, [showDemoIntro])

  const handleLaunchDemoInterview = () => {
    if (!DEMO_INTERVIEW_URL) {
      return
    }

    window.open(DEMO_INTERVIEW_URL, '_blank', 'noopener,noreferrer')
    setShowDemoIntro(false)
  }

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      setShowDemoIntro(false)
    }
  }

  return (
    <>
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-eyebrow">AI Workforce Intelligence Platform</div>
            <h1>The AI Workforce<br/>Intelligence Platform</h1>
            <p className="hero-subtitle">
              One conversational AI platform connecting hiring, skill assessment, career
              development, and enterprise knowledge — generating actionable intelligence
              across the entire employee lifecycle.
            </p>
            <div className="hero-cta">
              <button className="btn-primary btn-large" onClick={() => scrollToSection('platform')}>Explore the Platform</button>
              <button className="btn-secondary btn-large hero-secondary-btn" onClick={() => openBookDemo('Book a Demo')}>Book a Demo</button>
            </div>
            <div className="trust-indicators">
              Trusted by enterprise teams &bull; Fortune 100 deployments &bull; Big 4 validated
            </div>
            <div className="hero-try">
              <p className="hero-try-label">No sales pitch. See the platform in action.</p>
              <button className="hero-try-btn" onClick={() => setShowDemoIntro(true)}>
                <span className="hero-try-play">&#9654;</span> Try SmartHire Now
              </button>
            </div>
          </div>
        </div>
      </section>

      {showDemoIntro && (
        <div className="hero-modal-backdrop" onClick={handleBackdropClick}>
          <div className="hero-modal" role="dialog" aria-modal="true" aria-labelledby="demo-interview-title">
            <button className="hero-modal__close" onClick={() => setShowDemoIntro(false)} aria-label="Close dialog">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M5 5L15 15M15 5L5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>

            <h2 id="demo-interview-title" className="hero-modal__title">Experience the Platform</h2>
            <p className="hero-modal__subtitle">
              You're about to experience a live AI-powered interview with Sia.{' '}
              <strong>Most interviews ask the same questions to every candidate. Sia doesn't.</strong>
            </p>
            <p className="hero-modal__copy">
              Sia is PossoBuild's conversational AI interviewer. She listens to your responses, understands context, and asks follow-up questions in real time — adapting the conversation based on what you say, just like a skilled human interviewer would. No scripts. No fixed question order. Every conversation is unique.
            </p>
            <p className="hero-modal__copy">
              In this 5-minute demo, you'll experience how SmartHire evaluates candidates through adaptive, human-like conversations — and see why organisations are rethinking how they identify top talent.
            </p>
            <p className="hero-modal__list-intro">Here's what to expect:</p>

            <ul className="hero-modal__list">
              {DEMO_CONTEXT_POINTS.map((point) => (
                <li key={point.title} className="hero-modal__item">
                  <h3>{point.title}</h3>
                  <p>{point.body}</p>
                </li>
              ))}
            </ul>

            <div className="hero-modal__actions">
              <button className="btn-secondary hero-modal__secondary" onClick={() => setShowDemoIntro(false)}>
                Close
              </button>
              <button className="btn-primary hero-modal__primary" onClick={handleLaunchDemoInterview} disabled={!DEMO_INTERVIEW_URL}>
                Take a Demo Interview
              </button>
            </div>

            {!DEMO_INTERVIEW_URL && (
              <p className="hero-modal__hint">Set VITE_DEMO_INTERVIEW_URL to enable the demo interview launch.</p>
            )}
          </div>
        </div>
      )}
    </>
  )
}
